import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useChatPanel } from "./ChatPanelContext";
import type { ChatPanelProps } from "./types";

function OverlayChatPanel({
  children,
  className = "",
}: Omit<ChatPanelProps, "inline">) {
  const { isOpen, closePanel } = useChatPanel();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    function handlePointerDown(e: PointerEvent) {
      const target = e.target as Element;
      if (panelRef.current && !panelRef.current.contains(target)) {
        // Don't close if clicking inside a Radix UI portal (popover, dropdown, etc.)
        // Radix wraps all portal content in a div with this attribute
        const isInRadixPortal = target.closest?.(
          "[data-radix-popper-content-wrapper]",
        );
        if (!isInRadixPortal) {
          closePanel();
        }
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen, closePanel]);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={panelRef}
          key="chat-panel"
          role="dialog"
          aria-label="AI Assistant"
          className={`fixed top-3 right-3 bottom-3 w-[500px] max-w-[90vw] z-40 bg-surface rounded-2xl shadow-xl flex flex-col overflow-hidden ${className}`}
          initial={{ y: "100%", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: "100%", opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

export function ChatPanelRoot({
  children,
  className = "",
  inline,
}: ChatPanelProps) {
  if (inline) {
    return (
      <div
        className={`flex flex-col h-full w-full bg-surface overflow-hidden ${className}`}
      >
        {children}
      </div>
    );
  }

  return <OverlayChatPanel className={className}>{children}</OverlayChatPanel>;
}
